import { useEffect, useRef } from "react";

const PERF_ENABLED =
  typeof window !== "undefined" &&
  (import.meta.env.DEV || localStorage.getItem("dashboard_perf") === "1");

/**
 * Tracks render count and lifespan for a modal component.
 *
 * Logs to the browser console in DEV mode, or when:
 *   localStorage.setItem('dashboard_perf', '1'); location.reload();
 *
 * Usage:
 *   useModalPerf("CreateProjectModal");
 *
 * Output on unmount:
 *   [ModalPerf] CreateProjectModal — 1 renders in 3241ms
 *   If renders > 4: warning that re-renders are high (investigate memo/useCallback)
 */
export function useModalPerf(label: string) {
  const renderCount = useRef(0);
  const mountTime = useRef(performance.now());

  // Increment on every render — safe because refs never cause re-renders.
  renderCount.current += 1;

  // Single stable effect: runs once on mount, reports on unmount.
  // Depends on label (always a string literal — never changes for a given modal).
  useEffect(() => {
    if (!PERF_ENABLED) return;

    const elapsed = (performance.now() - mountTime.current).toFixed(1);
    console.groupCollapsed(
      `%c[ModalPerf] ${label} mounted in ${elapsed}ms`,
      "color: #38bdf8; font-weight: bold",
    );
    console.log("Mount latency:", `${elapsed}ms`);
    console.groupEnd();

    return () => {
      const lifespan = (performance.now() - mountTime.current).toFixed(0);
      const renders = renderCount.current;

      console.groupCollapsed(
        `%c[ModalPerf] ${label} — ${renders} render${renders === 1 ? "" : "s"} in ${lifespan}ms`,
        renders > 4
          ? "color: #fb923c; font-weight: bold"
          : "color: #a78bfa; font-weight: bold",
      );
      console.table({
        "Total renders while open": renders,
        "Lifespan (ms)": lifespan,
      });
      if (renders > 4) {
        console.warn(
          `[ModalPerf] ${label} had ${renders} renders. ` +
            "Check that callback props (onClose, onXxx) are wrapped in useCallback in the parent.",
        );
      }
      console.groupEnd();
    };
  }, [label]);
}
